import axios, { AxiosInstance } from "axios";
import { API_CONFIG } from "../config";
import {
  AdminUser,
  Device,
  DeviceAnalytics,
  LoginCredentials,
  RegisterData,
  ApiResponse,
  PaginatedResponse,
  UsersResponse,
  FilterOptions,
  SortOptions,
  ExportOptions,
  DashboardStats,
  SystemConfig,
  AuditLog,
  Permission,
  HtmlContent,
  HtmlContentFormData,
  Category,
  CategoryFormData,
  CreativeLookTemplate,
  CreativeLookTemplateFormData,
  DashboardDaily,
} from "../types";

type HtmlContentType = "contact_us" | "privacy_policy" | "terms_conditions";

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: API_CONFIG.TIMEOUTS.DEFAULT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("admin_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error instanceof Error ? error : new Error(String(error)))
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        // Don't redirect to login for public endpoints
        const isPublicEndpoint = error.config?.url?.includes('/public/') || 
                                 error.config?.url?.includes('/devices/languages');
        
        if (error.response?.status === 401 && !isPublicEndpoint) {
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_user");
          window.location.href = "/login";
        }
        return Promise.reject(error instanceof Error ? error : new Error(String(error)));
      }
    );
  }

  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    const response = await this.api.post("/api/admin/auth/login", credentials);
    return response.data;
  }

  async register(data: RegisterData): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.post("/api/admin/auth/register", data);
    return response.data;
  }

  async logout(): Promise<ApiResponse<void>> {
    const response = await this.api.post("/api/admin/auth/logout");
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.get("/api/admin/auth/me");
    return response.data;
  }

  // Admin user management
  async getUsers(filters?: FilterOptions, sort?: SortOptions): Promise<ApiResponse<UsersResponse>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    if (sort) {
      params.append("sortBy", sort.field);
      params.append("sortOrder", sort.order);
    }
    const response = await this.api.get(`/api/admin/users?${params}`);
    return response.data;
  }

  async getAdmins(page = 1, limit = 10, filters?: FilterOptions): Promise<ApiResponse<PaginatedResponse<AdminUser>>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await this.api.get(`/api/admin/users?${params}`);
    return response.data;
  }

  async getAdmin(id: string): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.get(`/api/admin/users/${id}`);
    return response.data;
  }

  async createUser(data: RegisterData): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.post("/api/admin/users", data);
    return response.data;
  }

  async createAdmin(data: RegisterData): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.post("/api/admin/users", data);
    return response.data;
  }

  async updateUser(id: string, data: Partial<RegisterData>): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.put(`/api/admin/users/${id}`, data);
    return response.data;
  }

  async updateAdmin(id: string, data: Partial<RegisterData>): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.put(`/api/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/users/${id}`);
    return response.data;
  }

  async deleteAdmin(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/users/${id}`);
    return response.data;
  }

  async toggleAdminStatus(id: string): Promise<ApiResponse<AdminUser>> {
    const response = await this.api.patch(`/api/admin/users/${id}/toggle-status`);
    return response.data;
  }

  // App users (Firebase authenticated users)
  async getAppUsers(page = 1, limit = 10, filters?: FilterOptions): Promise<ApiResponse<{ users: any[]; pagination: any }>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (filters?.search) {
      params.append("search", filters.search);
    }
    if (filters?.signInMethod) {
      params.append("signInMethod", filters.signInMethod);
    }
    if (filters?.sortBy) {
      params.append("sortBy", filters.sortBy);
    }
    if (filters?.sortOrder) {
      params.append("sortOrder", filters.sortOrder);
    }

    const response = await this.api.get(`/api/admin/users/app-users?${params}`);
    return response.data;
  }

  async getAppUser(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/users/app-users/${id}`);
    return response.data;
  }

  async getUserTransactions(userId: string, page = 1, limit = 20): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/users/app-users/${userId}/transactions`, {
      params: { page, limit },
    });
    return response.data;
  }

  async addCreditsToUser(userId: string, amount: number, reason?: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/admin/users/app-users/${userId}/credits/add`, {
      amount,
      reason: reason ?? "admin_adjustment",
    });
    return response.data;
  }

  // Analytics
  async getDailySwapAnalytics(startDate?: string, endDate?: string, swapType?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (swapType) params.swapType = swapType;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/daily", { params });
    return response.data;
  }

  async getDeviceSwapAnalytics(startDate?: string, endDate?: string, swapType?: string, limit = 50, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (swapType) params.swapType = swapType;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/devices", { params });
    return response.data;
  }

  async getUserSwapAnalytics(startDate?: string, endDate?: string, swapType?: string, limit = 50, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (swapType) params.swapType = swapType;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/users", { params });
    return response.data;
  }

  async getSwapAnalyticsOverview(startDate?: string, endDate?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/overview", { params });
    return response.data;
  }

  async getHourlySwapAnalytics(startDate?: string, endDate?: string, swapType?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (swapType) params.swapType = swapType;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/hourly", { params });
    return response.data;
  }

  async getTemplateSwapAnalytics(startDate?: string, endDate?: string, limit = 50, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/templates", { params });
    return response.data;
  }

  async getSuccessRateAnalytics(startDate?: string, endDate?: string, swapType?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (swapType) params.swapType = swapType;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/swaps/success-rate", { params });
    return response.data;
  }

  // Creative Look analytics
  async getCreativeLookOverview(startDate?: string, endDate?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/creative-looks/overview", { params });
    return response.data;
  }

  async getDailyCreativeLookAnalytics(startDate?: string, endDate?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/creative-looks/daily", { params });
    return response.data;
  }

  async getCreativeLookTemplateAnalytics(startDate?: string, endDate?: string, limit = 20, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/creative-looks/templates", { params });
    return response.data;
  }

  // Purchase analytics
  async getPurchaseAnalyticsOverview(startDate?: string, endDate?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/purchases/overview", { params });
    return response.data;
  }

  async getDailyPurchaseAnalytics(startDate?: string, endDate?: string, platform?: string, productId?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (platform) params.platform = platform;
    if (productId) params.productId = productId;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/purchases/daily", { params });
    return response.data;
  }

  async getProductPurchaseAnalytics(startDate?: string, endDate?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/purchases/products", { params });
    return response.data;
  }

  async getUserPurchaseAnalytics(startDate?: string, endDate?: string, limit = 50, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { limit };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/purchases/users", { params });
    return response.data;
  }

  async getPurchasesByDate(date: string, platform?: string, productId?: string, timezone?: string): Promise<ApiResponse<any>> {
    const params: any = { date };
    if (platform) params.platform = platform;
    if (productId) params.productId = productId;
    if (timezone) params.timezone = timezone;
    const response = await this.api.get("/api/admin/analytics/purchases/by-date", { params });
    return response.data;
  }

  // Credit configuration
  async getCreditConfig(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/credits/config");
    return response.data;
  }

  async updateCreditConfig(data: { costs?: any; rewards?: any; freeUser?: any; productCredits?: any; productDiscounts?: any; productPrices?: any; productTonPrices?: any }): Promise<ApiResponse<any>> {
    const response = await this.api.put("/api/admin/credits/config", data);
    return response.data;
  }

  async getCreditConfigHistory(page = 1, limit = 10): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await this.api.get(`/api/admin/credits/config/history?${params}`);
    return response.data;
  }

  // Feature configuration (v2 - per-feature settings)
  async getAllFeatureConfigs(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get("/api/admin/features/config");
    return response.data;
  }

  async getFeatureConfig(featureId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/features/config/${featureId}`);
    return response.data;
  }

  async updateFeatureConfig(featureId: string, data: {
    ads?: {
      isAllowAds?: boolean;
      adType?: 'none' | 'interstitial' | 'rewarded';
      androidAdUnitId?: string;
      iosAdUnitId?: string;
    };
    credits?: {
      isAllowCreditUsage?: boolean;
      creditCost?: number;
    };
    dailyQuota?: {
      isDailyLimitForFreeUsers?: boolean;
      dailyQuota?: number;
    };
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/features/config/${featureId}`, data);
    return response.data;
  }

  // Permissions management
  async getPermissions(): Promise<ApiResponse<Permission[]>> {
    const response = await this.api.get("/api/admin/permissions");
    return response.data;
  }

  async getPermission(id: string): Promise<ApiResponse<Permission>> {
    const response = await this.api.get(`/api/admin/permissions/${id}`);
    return response.data;
  }

  async createPermission(data: Omit<Permission, "id">): Promise<ApiResponse<Permission>> {
    const response = await this.api.post("/api/admin/permissions", data);
    return response.data;
  }

  async updatePermission(id: string, data: Partial<Omit<Permission, "id">>): Promise<ApiResponse<Permission>> {
    const response = await this.api.put(`/api/admin/permissions/${id}`, data);
    return response.data;
  }

  async deletePermission(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/permissions/${id}`);
    return response.data;
  }

  // Device management
  async getDevices(page = 1, limit = 10, filters?: FilterOptions, sort?: SortOptions): Promise<ApiResponse<PaginatedResponse<Device>>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    if (sort) {
      params.append("sortBy", sort.field);
      params.append("sortOrder", sort.order);
    }
    const response = await this.api.get(`/api/devices/admin/all?${params}`);
    return response.data;
  }

  // Full device record for the detail view.
  // NOTE: takes the deviceId string, NOT the Mongo _id — the server looks the
  // device up by deviceId.
  async getDevice(deviceId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/devices/admin/detail/${encodeURIComponent(deviceId)}`);
    return response.data;
  }

  // Block/unblock also key off deviceId (findByDeviceId server-side).
  async blockDevice(deviceId: string, reason: string): Promise<ApiResponse<Device>> {
    const response = await this.api.post(`/api/devices/admin/block/${encodeURIComponent(deviceId)}`, { reason });
    return response.data;
  }

  async unblockDevice(deviceId: string): Promise<ApiResponse<Device>> {
    const response = await this.api.post(`/api/devices/admin/unblock/${encodeURIComponent(deviceId)}`);
    return response.data;
  }

  async deleteDevice(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/devices/admin/${id}`);
    return response.data;
  }

  async exportDevices(options: ExportOptions): Promise<Blob> {
    const response = await this.api.post("/api/devices/admin/export", options, {
      responseType: "blob",
    });
    return response.data;
  }

  // Analytics and dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.api.get("/api/admin/dashboard/stats");
    return response.data;
  }

  async getDashboardDaily(days = 30): Promise<ApiResponse<DashboardDaily>> {
    const response = await this.api.get("/api/admin/dashboard/daily", { params: { days } });
    return response.data;
  }

  async getDeviceAnalytics(): Promise<ApiResponse<DeviceAnalytics>> {
    const response = await this.api.get("/api/devices/admin/statistics");
    return response.data;
  }

  async getDeviceAnalyticsChart(timeRange: "7d" | "30d" | "90d" | "1y"): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/devices/admin/analytics/chart?range=${timeRange}`);
    return response.data;
  }

  // System configuration
  async getSystemConfig(): Promise<ApiResponse<SystemConfig[]>> {
    const response = await this.api.get("/api/admin/config");
    return response.data;
  }

  async updateSystemConfig(key: string, value: any): Promise<ApiResponse<SystemConfig>> {
    const response = await this.api.put(`/api/admin/config/${key}`, { value });
    return response.data;
  }

  // Audit logs
  async getAuditLogs(page = 1, limit = 10, filters?: FilterOptions): Promise<ApiResponse<PaginatedResponse<AuditLog>>> {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("limit", limit.toString());
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await this.api.get(`/api/admin/audit-logs?${params}`);
    return response.data;
  }

  // Health check
  async getHealthStatus(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/health");
    return response.data;
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem("admin_token", token);
    this.api.defaults.headers.Authorization = `Bearer ${token}`;
  }

  removeAuthToken(): void {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    delete this.api.defaults.headers.Authorization;
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("admin_token");
  }

  // HTML Content Management
  async getHtmlContent(type: HtmlContentType): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.get(`/api/html-content/content/${type}`);
    return response.data;
  }

  async getAllHtmlContent(): Promise<ApiResponse<HtmlContent[]>> {
    // Add raw=true to get full multilingual data for admin editing
    const response = await this.api.get("/api/html-content/admin/all", { params: { raw: "true" } });
    return response.data;
  }

  async upsertHtmlContent(data: HtmlContentFormData): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.post("/api/html-content/admin/upsert", data);
    return response.data;
  }

  async toggleHtmlContentStatus(type: HtmlContentType, isActive: boolean): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.patch(`/api/html-content/admin/${type}/toggle`, { isActive });
    return response.data;
  }

  async getHtmlContentHistory(type: "contact_us" | "privacy_policy" | "terms_conditions"): Promise<ApiResponse<HtmlContent[]>> {
    const response = await this.api.get(`/api/html-content/admin/${type}/history`);
    return response.data;
  }

  async initializeDefaultHtmlContent(): Promise<ApiResponse<HtmlContent[]>> {
    const response = await this.api.post("/api/html-content/admin/initialize");
    return response.data;
  }

  // Version Management API methods
  async createVersion(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/versions/admin/create", data);
    return response.data;
  }

  async getAllVersions(params?: any): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/versions/admin/all", { params });
    return response.data;
  }

  async getVersionById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/versions/admin/${id}`);
    return response.data;
  }

  async updateVersion(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/versions/admin/${id}`, data);
    return response.data;
  }

  async deleteVersion(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/versions/admin/${id}`);
    return response.data;
  }

  async toggleUpdateStatus(id: string, isUpdateAvailable: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/versions/admin/${id}/toggle-update`, { isUpdateAvailable });
    return response.data;
  }

  async toggleForceUpdate(id: string, isForceUpdate: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/versions/admin/${id}/toggle-force-update`, { isForceUpdate });
    return response.data;
  }

  // Public version check methods
  async getLatestVersion(platform?: string): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/versions/latest", {
      params: platform ? { platform } : {},
    });
    return response.data;
  }

  async checkUpdateRequired(currentVersion: string, platform?: string): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/versions/check-update", {
      params: { currentVersion, platform },
    });
    return response.data;
  }

  // Queue management methods
  async getQueueStatus(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/queue/status");
    return response.data;
  }

  async getJobStatus(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/queue/jobs/${jobId}`);
    return response.data;
  }

  async cancelJob(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.api.post(`/api/admin/queue/jobs/${jobId}/cancel`);
    return response.data;
  }

  async stopAllJobs(): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/queue/jobs/stop-all");
    return response.data;
  }

  // Public content methods (no authentication required)
  async getPublicPrivacyPolicy(languageCode = "EN"): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.get("/api/html-content/public/privacy_policy", { params: { lang: languageCode } });
    return response.data;
  }

  async getPublicTermsConditions(languageCode = "EN"): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.get("/api/html-content/public/terms_conditions", { params: { lang: languageCode } });
    return response.data;
  }

  async getPublicContactUs(languageCode = "EN"): Promise<ApiResponse<HtmlContent>> {
    const response = await this.api.get("/api/html-content/public/contact_us", { params: { lang: languageCode } });
    return response.data;
  }

  // Category Management API methods
  async getAllCategories(filters?: { isActive?: boolean; search?: string }): Promise<ApiResponse<Category[]>> {
    // Add raw=true to get full multilingual data for admin editing
    const params = { ...filters, raw: "true" };
    const response = await this.api.get("/api/admin/categories", { params });
    return response.data;
  }

  async getActiveCategories(): Promise<ApiResponse<Category[]>> {
    // Add raw=true to get full multilingual data for admin editing
    const response = await this.api.get("/api/admin/categories/active", { params: { raw: "true" } });
    return response.data;
  }

  async getCategoryById(id: string): Promise<ApiResponse<Category>> {
    // Add raw=true to get full multilingual data for admin editing
    const response = await this.api.get(`/api/admin/categories/${id}`, { params: { raw: "true" } });
    return response.data;
  }

  async getCategoryBySlug(slug: string): Promise<ApiResponse<Category>> {
    // Add raw=true to get full multilingual data for admin editing
    const response = await this.api.get(`/api/admin/categories/slug/${slug}`, { params: { raw: "true" } });
    return response.data;
  }

  async createCategory(data: CategoryFormData): Promise<ApiResponse<Category>> {
    const response = await this.api.post("/api/admin/categories", data);
    return response.data;
  }

  async updateCategory(id: string, data: Partial<CategoryFormData>): Promise<ApiResponse<Category>> {
    const response = await this.api.put(`/api/admin/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/categories/${id}`);
    return response.data;
  }

  // Creative Look Template Management API methods
  async getAllCreativeLookTemplates(filters?: {
    category?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{ templates: CreativeLookTemplate[]; total: number }>> {
    const response = await this.api.get("/api/admin/creative-look-templates", { params: filters });
    return response.data;
  }

  async getCreativeLookTemplateById(id: string): Promise<ApiResponse<CreativeLookTemplate>> {
    const response = await this.api.get(`/api/admin/creative-look-templates/${id}`);
    return response.data;
  }

  async createCreativeLookTemplate(data: CreativeLookTemplateFormData): Promise<ApiResponse<CreativeLookTemplate>> {
    const response = await this.api.post("/api/admin/creative-look-templates", data);
    return response.data;
  }

  async updateCreativeLookTemplate(id: string, data: Partial<CreativeLookTemplateFormData>): Promise<ApiResponse<CreativeLookTemplate>> {
    const response = await this.api.patch(`/api/admin/creative-look-templates/${id}`, data);
    return response.data;
  }

  async deleteCreativeLookTemplate(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/creative-look-templates/${id}`);
    return response.data;
  }

  async uploadCreativeLookImage(id: string, file: File): Promise<ApiResponse<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append("image", file);
    const response = await this.api.post(`/api/admin/creative-look-templates/${id}/image`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  async getCreativeLookCategories(): Promise<ApiResponse<Array<{ _id: string; count: number }>>> {
    const response = await this.api.get("/api/admin/creative-look-templates/stats/categories");
    return response.data;
  }

  // Ad configuration
  async getAdConfig(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/ads/config");
    return response.data;
  }

  async updateAdConfig(config: any): Promise<ApiResponse<any>> {
    const response = await this.api.put("/api/admin/ads/config", config);
    return response.data;
  }

  async toggleAdsEnabled(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/ads/toggle", { enabled });
    return response.data;
  }

  // App configuration
  async getAppConfig(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/app/config");
    return response.data;
  }

  async updateAppConfig(config: any): Promise<ApiResponse<any>> {
    const response = await this.api.put("/api/admin/app/config", config);
    return response.data;
  }

  async toggleVideoSwap(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-video-swap", { enabled });
    return response.data;
  }

  async toggleVideoSwapPaused(paused: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-video-swap-paused", { paused });
    return response.data;
  }

  async toggleGroupFaceSwap(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-group-face-swap", { enabled });
    return response.data;
  }

  async toggleRestoreImage(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-restore-image", { enabled });
    return response.data;
  }

  async toggleEnhanceImage(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-enhance-image", { enabled });
    return response.data;
  }

  async toggleCreativeLooks(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-creative-looks", { enabled });
    return response.data;
  }

  async toggleNsfwCheck(enabled: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/toggle-nsfw-check", { enabled });
    return response.data;
  }

  async updateNsfwSettings(settings: {
    nsfwBlockedClasses?: string[];
    nsfwDefaultThreshold?: number;
    nsfwClassThresholds?: Record<string, number>;
    nsfwStoreImages?: boolean;
    nsfwIncidentRetentionMinutes?: number;
    csamCheckEnabled?: boolean;
    csamAgeThreshold?: number;
    csamAutoBlockEnabled?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/nsfw-settings", settings);
    return response.data;
  }

  // Which engines are usable + the active/forced engine.
  async getNsfwEngineHealth(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/app/nsfw-engine-health");
    return response.data;
  }

  // Google Vision NSFW settings (used for reviewer/test devices).
  // An empty apiKey means "leave the existing key unchanged".
  async updateVisionNsfwSettings(settings: {
    enabled?: boolean;
    apiKey?: string;
    blockOnError?: boolean;
    videoFrameCount?: number;
    categories?: Record<string, string>;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/vision-nsfw-settings", settings);
    return response.data;
  }

  // NSFW incident monitoring
  async getNsfwStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/nsfw/stats");
    return response.data;
  }

  async getNsfwOffenders(limit = 100): Promise<ApiResponse<any[]>> {
    const response = await this.api.get(`/api/admin/nsfw/offenders?limit=${limit}`);
    return response.data;
  }

  async getNsfwIncidents(params: {
    page?: number;
    limit?: number;
    deviceId?: string;
    userId?: string;
    requestType?: string;
    matchedClass?: string;
    from?: string;
    to?: string;
  } = {}): Promise<ApiResponse<any>> {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const response = await this.api.get(`/api/admin/nsfw/incidents${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  getNsfwIncidentImageUrl(incidentId: string): string {
    // NOTE: Don't use this URL directly in an <img src=...> tag — the
    // endpoint is admin-auth-protected and <img> requests can't carry the
    // JWT header. Use fetchNsfwIncidentImageBlob() instead and render the
    // resulting object URL.
    return `${this.baseURL}/api/admin/nsfw/incidents/${incidentId}/image`;
  }

  /**
   * Fetch a protected NSFW incident image via authenticated axios and
   * return an object URL the browser can render in an <img> tag.
   *
   * The caller MUST revoke the returned URL via URL.revokeObjectURL()
   * once it's no longer needed, to avoid leaking blobs.
   */
  async fetchNsfwIncidentImageBlob(incidentId: string): Promise<string> {
    const response = await this.api.get(
      `/api/admin/nsfw/incidents/${incidentId}/image`,
      { responseType: 'blob' }
    );
    return URL.createObjectURL(response.data as Blob);
  }

  async blockNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/block", payload);
    return response.data;
  }

  async unblockNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/unblock", payload);
    return response.data;
  }

  // Grant an NSFW moderation bypass (whitelist) to a device and/or user.
  // Bypassed offenders skip the NSFW/CSAM check entirely on future uploads.
  async bypassNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
    reason?: string;
    notify?: boolean;
    notifyTitle?: string;
    notifyBody?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/bypass", payload);
    return response.data;
  }

  async unbypassNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/unbypass", payload);
    return response.data;
  }

  // Force ALWAYS-ON Google Vision scanning for a device and/or user — the
  // opposite of a bypass. Overrides the global engine toggle and any bypass.
  async forceVisionNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
    userEmail?: string;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/force-vision", payload);
    return response.data;
  }

  async unforceVisionNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
    userEmail?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/unforce-vision", payload);
    return response.data;
  }

  async deleteNsfwIncident(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/nsfw/incidents/${id}`);
    return response.data;
  }

  async deleteNsfwOffender(payload: {
    deviceId?: string;
    userId?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/nsfw/offenders/delete", payload);
    return response.data;
  }

  // User-submitted content reports
  async getUserReportStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/reports/stats");
    return response.data;
  }

  async getUserReports(params: {
    page?: number;
    limit?: number;
    status?: string;
    category?: string;
  } = {}): Promise<ApiResponse<any>> {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
    });
    const qs = q.toString();
    const response = await this.api.get(`/api/admin/reports${qs ? `?${qs}` : ''}`);
    return response.data;
  }

  async updateUserReportStatus(
    id: string,
    payload: { status?: string; actionNotes?: string }
  ): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/admin/reports/${id}`, payload);
    return response.data;
  }

  async deleteUserReport(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/reports/${id}`);
    return response.data;
  }

  // Age-detection debug — uploads an image, returns estimated age per face.
  async testAgeDetection(file: File): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('image', file);
    const response = await this.api.post(
      '/api/admin/age-test',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // NSFW pipeline debug — raw NudeNet detections.
  async testNsfwRaw(file: File): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('image', file);
    const response = await this.api.post(
      '/api/admin/nsfw-test/raw',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  // NSFW pipeline debug — full decision with caller-specified config.
  async testNsfwDecision(
    file: File,
    config: {
      blockedClasses: string[];
      defaultThreshold: number;
      classThresholds: Record<string, number>;
      csamCheckEnabled: boolean;
      csamAgeThreshold: number;
    }
  ): Promise<ApiResponse<any>> {
    const form = new FormData();
    form.append('image', file);
    form.append('blockedClasses', JSON.stringify(config.blockedClasses));
    form.append('defaultThreshold', String(config.defaultThreshold));
    form.append('classThresholds', JSON.stringify(config.classThresholds));
    form.append('csamCheckEnabled', String(config.csamCheckEnabled));
    form.append('csamAgeThreshold', String(config.csamAgeThreshold));
    const response = await this.api.post(
      '/api/admin/nsfw-test/decision',
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  }

  async updateCleanupSettings(settings: { autoCleanupEnabled?: boolean; cleanupDelayMinutes?: number; videoCleanupDelayMinutes?: number }): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/cleanup-settings", settings);
    return response.data;
  }

  async updateWelcomeCredits(welcomeCredits: number): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/app/welcome-credits", { welcomeCredits });
    return response.data;
  }

  // Language management
  async getLanguages(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get("/api/admin/languages");
    return response.data;
  }

  async createLanguage(data: { name: string; code: string; nativeName: string; flag?: string; isDefault?: boolean }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/languages", data);
    return response.data;
  }

  async updateLanguage(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/languages/${id}`, data);
    return response.data;
  }

  async deleteLanguage(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/languages/${id}`);
    return response.data;
  }

  async setDefaultLanguage(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/admin/languages/${id}/set-default`);
    return response.data;
  }

  async toggleLanguageActive(id: string, isActive: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/admin/languages/${id}/toggle-active`, { isActive });
    return response.data;
  }

  // String translation management
  // type: 'APP_STRINGS' (mobile app) or 'WEB_STRINGS' (web app)
  async getStringsByLanguage(languageCode: string, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/strings/${languageCode}`, { params: { type } });
    return response.data;
  }

  async getStringsByLanguageAndGroup(languageCode: string, group: string, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/devices/strings/${languageCode}/${group}`, { params: { type } });
    return response.data;
  }

  async getKeysByGroup(group: string, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<string[]>> {
    const response = await this.api.get(`/api/admin/strings/keys/${group}`, { params: { type } });
    return response.data;
  }

  async getTranslationsByKey(group: string, key: string, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/strings/translations/${group}/${key}`, { params: { type } });
    return response.data;
  }

  async upsertString(data: { languageCode: string; group: string; key: string; value: string; description?: string; type?: 'APP_STRINGS' | 'WEB_STRINGS' }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/strings", { ...data, type: data.type || 'APP_STRINGS' });
    return response.data;
  }

  async bulkUpsertStrings(languageCode: string, strings: Array<{ group: string; key: string; value: string; description?: string }>, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/strings/bulk", { languageCode, strings, type });
    return response.data;
  }

  async deleteString(languageCode: string, group: string, key: string, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/strings/${languageCode}/${group}/${key}`, { params: { type } });
    return response.data;
  }

  async updateLanguageData(languageCode: string, data: any, type: 'APP_STRINGS' | 'WEB_STRINGS' = 'APP_STRINGS'): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/strings/${languageCode}`, { data, type }, { params: { type } });
    return response.data;
  }

  async toggleCategoryStatus(id: string): Promise<ApiResponse<Category>> {
    const response = await this.api.patch(`/api/admin/categories/${id}/toggle-status`);
    return response.data;
  }

  // Translation API
  async translateText(text: string, targetLanguage: string, sourceLanguage = "en"): Promise<ApiResponse<{ translatedText: string }>> {
    const response = await this.api.post("/api/admin/translate", {
      text,
      targetLanguage,
      sourceLanguage,
    });
    return response.data;
  }

  async translateHtml(html: string, targetLanguage: string, sourceLanguage = "en"): Promise<ApiResponse<{ translatedHtml: string }>> {
    const response = await this.api.post("/api/admin/translate/html", {
      html,
      targetLanguage,
      sourceLanguage,
    });
    return response.data;
  }

  async addTemplateImage(
    categoryId: string,
    imageData: {
      imageUrl: string;
      imageName?: string;
      fileType?: "image" | "video" | "gif";
      mimeType?: string;
      fileSize?: number;
      order?: number;
      isPremium?: boolean;
      isGroupImage?: boolean;
    }
  ): Promise<ApiResponse<Category>> {
    const response = await this.api.post(`/api/admin/categories/${categoryId}/template-images`, imageData);
    return response.data;
  }

  async addMultipleTemplateImages(
    categoryId: string,
    imagesData: Array<{
      imageUrl: string;
      imageName?: string;
      fileType?: "image" | "video" | "gif";
      mimeType?: string;
      fileSize?: number;
      order?: number;
      isPremium?: boolean;
      isGroupImage?: boolean;
    }>
  ): Promise<ApiResponse<Category>> {
    const response = await this.api.post(`/api/admin/categories/${categoryId}/template-images/batch`, { images: imagesData });
    return response.data;
  }

  async removeTemplateImage(categoryId: string, imageId: string): Promise<ApiResponse<Category>> {
    const response = await this.api.delete(`/api/admin/categories/${categoryId}/template-images/${imageId}`);
    return response.data;
  }

  async reorderTemplateImages(categoryId: string, imageOrders: Array<{ imageId: string; order: number }>): Promise<ApiResponse<Category>> {
    const response = await this.api.put(`/api/admin/categories/${categoryId}/template-images/reorder`, { imageOrders });
    return response.data;
  }

  async updateTemplateImagePremium(categoryId: string, imageId: string, isPremium: boolean): Promise<ApiResponse<Category>> {
    const response = await this.api.patch(`/api/admin/categories/${categoryId}/template-images/${imageId}/premium`, { isPremium });
    return response.data;
  }

  async updateTemplateImageGroupImage(categoryId: string, imageId: string, isGroupImage: boolean): Promise<ApiResponse<Category>> {
    const response = await this.api.patch(`/api/admin/categories/${categoryId}/template-images/${imageId}/group-image`, { isGroupImage });
    return response.data;
  }

  async getCategory(categoryId: string): Promise<ApiResponse<Category>> {
    return this.getCategoryById(categoryId);
  }

  // File Upload API methods
  async uploadCategoryImage(file: File): Promise<ApiResponse<{ url: string; filename: string; originalName: string; size: number; mimetype: string; type: string }>> {
    const formData = new FormData();
    formData.append("image", file);

    const response = await this.api.post("/api/admin/upload/category-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // Convert relative URL to full URL if needed
    if (response.data.success && response.data.data) {
      const url = response.data.data.url;
      if (url.startsWith("/")) {
        response.data.data.url = `${this.baseURL}${url}`;
      }
    }

    return response.data;
  }

  async uploadTemplateImage(file: File): Promise<ApiResponse<{ url: string; filename: string; originalName: string; size: number; mimetype: string; type: string }>> {
    const formData = new FormData();
    formData.append("image", file);

    const response = await this.api.post("/api/admin/upload/template-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // Convert relative URL to full URL if needed
    if (response.data.success && response.data.data) {
      const url = response.data.data.url;
      if (url.startsWith("/")) {
        response.data.data.url = `${this.baseURL}${url}`;
      }
    }

    return response.data;
  }

  // Offers banner upload (reuse category upload)
  async uploadOfferImage(file: File): Promise<ApiResponse<{ url: string; filename: string; originalName: string; size: number; mimetype: string; type: string }>> {
    const formData = new FormData();
    formData.append("image", file);

    const response = await this.api.post("/api/admin/upload/offer-banner", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    if (response.data.success && response.data.data) {
      const url = response.data.data.url;
      if (url.startsWith("/")) {
        response.data.data.url = `${this.baseURL}${url}`;
      }
    }

    return response.data;
  }

  // Push Notification API methods
  async sendNotificationToDevice(deviceId: string, notification: { title: string; body: string; imageUrl?: string }, data?: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/push/send-to-device", {
      deviceId,
      notification,
      data,
    });
    return response.data;
  }

  async sendNotificationToMultipleDevices(
    deviceIds: string[],
    notification: { title: string; body: string; imageUrl?: string },
    data?: Record<string, any>
  ): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/push/send-to-multiple", {
      deviceIds,
      notification,
      data,
    });
    return response.data;
  }

  async sendNotificationToFcmTokens(fcmTokens: string[], notification: { title: string; body: string; imageUrl?: string }, data?: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/push/send-to-tokens", {
      fcmTokens,
      notification,
      data,
    });
    return response.data;
  }

  async sendNotificationToAllDevices(notification: { title: string; body: string; imageUrl?: string }, data?: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/push/send-to-all", {
      notification,
      data,
    });
    return response.data;
  }

  async sendNotificationToTopic(topic: string, notification: { title: string; body: string; imageUrl?: string }, data?: Record<string, any>): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/push/send-to-topic", {
      topic,
      notification,
      data,
    });
    return response.data;
  }

  async getPushNotificationStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/push/stats");
    return response.data;
  }

  // Offers API
  async getOffersConfig(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/offers/config");
    return response.data;
  }

  async updateOffersConfig(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put("/api/admin/offers/config", data);
    return response.data;
  }

  // Output Images API
  async getOutputImages(
    page = 1,
    limit: number | "all" = 24,
    type: "all" | "images" | "videos" = "all",
    deviceId?: string,
    userId?: string,
    templateId?: string,
    country?: string
  ): Promise<ApiResponse<any>> {
    const params: any = { page, type };

    // Handle limit - if 'all', pass 'all' string, otherwise pass number
    if (limit === "all") {
      params.limit = "all";
    } else {
      params.limit = limit;
    }

    // Add deviceId if provided
    if (deviceId) {
      params.deviceId = deviceId;
    }

    // Add userId if provided
    if (userId) {
      params.userId = userId;
    }

    // Add templateId if provided
    if (templateId) {
      params.templateId = templateId;
    }

    // Add country if provided
    if (country) {
      params.country = country;
    }

    const response = await this.api.get("/api/admin/output-images", { params });
    return response.data;
  }

  async deleteOutputImage(filename: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/output-images/${encodeURIComponent(filename)}`);
    return response.data;
  }

  async deleteMultipleOutputImages(filenames: string[]): Promise<ApiResponse<any>> {
    const response = await this.api.delete("/api/admin/output-images", {
      data: { filenames },
    });
    return response.data;
  }

  async deleteOutputImagesOlderThan(minutes: number): Promise<ApiResponse<any>> {
    const response = await this.api.delete("/api/admin/output-images/older-than", {
      data: { minutes },
    });
    return response.data;
  }

  // FAQ Management API
  async getAllFAQs(): Promise<ApiResponse<any[]>> {
    const response = await this.api.get("/api/admin/faqs");
    return response.data;
  }

  async getFAQById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/admin/faqs/${id}`);
    return response.data;
  }

  async createFAQ(data: {
    question: Record<string, string>;
    answer: Record<string, string>;
    category?: string;
    tags?: string[];
    displayOrder?: number;
    isActive?: boolean;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/faqs", data);
    return response.data;
  }

  async updateFAQ(
    id: string,
    data: {
      question?: Record<string, string>;
      answer?: Record<string, string>;
      category?: string;
      tags?: string[];
      displayOrder?: number;
      isActive?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/admin/faqs/${id}`, data);
    return response.data;
  }

  async deleteFAQ(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/admin/faqs/${id}`);
    return response.data;
  }

  async toggleFAQStatus(id: string, isActive: boolean): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/admin/faqs/${id}/toggle-status`, { isActive });
    return response.data;
  }

  async reorderFAQs(orders: Array<{ id: string; displayOrder: number }>): Promise<ApiResponse<any>> {
    const response = await this.api.patch("/api/admin/faqs/reorder", { orders });
    return response.data;
  }

  // Featured Apps endpoints
  async getAllFeaturedApps(filters?: { isActive?: boolean; search?: string }): Promise<ApiResponse<any>> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await this.api.get(`/api/featured-apps/admin?${params}`);
    return response.data;
  }

  async getFeaturedAppById(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/api/featured-apps/admin/${id}`);
    return response.data;
  }

  async createFeaturedApp(data: any): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/featured-apps/admin", data);
    return response.data;
  }

  async updateFeaturedApp(id: string, data: any): Promise<ApiResponse<any>> {
    const response = await this.api.put(`/api/featured-apps/admin/${id}`, data);
    return response.data;
  }

  async deleteFeaturedApp(id: string): Promise<ApiResponse<void>> {
    const response = await this.api.delete(`/api/featured-apps/admin/${id}`);
    return response.data;
  }

  async toggleFeaturedAppActive(id: string): Promise<ApiResponse<any>> {
    const response = await this.api.patch(`/api/featured-apps/admin/${id}/toggle-active`);
    return response.data;
  }

  async reorderFeaturedApps(appOrders: Array<{ appId: string; displayOrder: number }>): Promise<ApiResponse<any>> {
    const response = await this.api.put("/api/featured-apps/admin/reorder", { appOrders });
    return response.data;
  }

  // Generic image upload method
  async uploadImage(formData: FormData): Promise<ApiResponse<{ url: string; filename?: string; originalName?: string; size?: number; mimetype?: string }>> {
    const response = await this.api.post("/api/admin/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // Convert relative URL to full URL if needed
    if (response.data.success && response.data.url) {
      const url = response.data.url;
      if (url.startsWith("/")) {
        response.data.url = `${this.baseURL}${url}`;
      }
    }

    return response.data;
  }

  // Creative Look Output Images
  async getCreativeLookOutput(
    page = 1,
    limit = 24,
    templateId?: string,
    userId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<any>> {
    const params: any = { page, limit };
    if (templateId) params.templateId = templateId;
    if (userId) params.userId = userId;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = await this.api.get("/api/admin/creative-looks/output", { params });
    return response.data;
  }

  async getCreativeLookOutputFilters(): Promise<ApiResponse<any>> {
    const response = await this.api.get("/api/admin/creative-looks/output/filters");
    return response.data;
  }

  async deleteCreativeLookOutput(jobId: string): Promise<ApiResponse<any>> {
    const response = await this.api.delete(`/api/admin/creative-looks/output/${jobId}`);
    return response.data;
  }

  async deleteMultipleCreativeLookOutputs(jobIds: string[]): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/creative-looks/output/delete-multiple", { jobIds });
    return response.data;
  }

  async deleteCreativeLookOutputsOlderThan(minutes: number): Promise<ApiResponse<any>> {
    const response = await this.api.post("/api/admin/creative-looks/output/delete-older-than", { minutes });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
